/* =============================================================================
   Grace Jacob – motion (styles in assets/gj-motion.css)

   Add to bag sequence:
     1. The button confirms itself: "Adding" + hairline → drawn tick "Added to your bag"
     2. No page-top loading bar for adds
     3. The bag drawer glides in a beat later on a warm backdrop, and its contents
        arrive in sequence (delivery bar fills from old to new, items, complete the look)
     4. The bag dot pulses once
   Hooks into Prestige's own events only (theme:loading:*, variant:add, cart:change,
   cart:error); theme.js is untouched.
   ============================================================================= */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var EASE_OUT = 'cubic-bezier(.22,.8,.24,1)';
  var EASE_IN_OUT = 'cubic-bezier(.45,0,.2,1)';
  var root = document.documentElement;

  var TICK = '<svg class="gj-atc__tick" viewBox="0 0 14 14" aria-hidden="true" focusable="false">' +
    '<path d="M1.5 7.4 5.3 11 12.5 3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ---------------------------------------------------------------------------
     1 + 2. Button states and quiet loading
     --------------------------------------------------------------------------- */
  var pending = null;      // { button, form } between submit and loading:start
  var active = null;       // button currently in the adding/added sequence
  var quietTimer = null;
  var resetTimer = null;

  function stateEl(button) {
    var el = button.querySelector(':scope > .gj-atc__state');
    if (!el) {
      el = document.createElement('span');
      el.className = 'gj-atc__state';
      el.setAttribute('aria-hidden', 'true');
      button.appendChild(el);
    }
    return el;
  }

  function resetButton(button) {
    if (!button) return;
    button.classList.remove('is-adding', 'is-added');
    if (active === button) active = null;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    var button = window.__gjAtcButton || event.submitter || form.querySelector('[type="submit"]');
    window.__gjAtcButton = null;
    // Buy-now (dynamic checkout) and gated size buttons are left alone
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    // Only full buttons get the sequence; small text-link "Add" buttons keep the theme behaviour
    if (!button.classList.contains('button')) return;
    pending = { button: button, form: form };
    root.classList.add('gj-quiet-load');
    clearTimeout(quietTimer);
    // If nothing starts loading (e.g. the form was invalid), lift the quiet class
    quietTimer = setTimeout(function () {
      if (pending) { pending = null; root.classList.remove('gj-quiet-load'); }
    }, 400);
  }, true);

  document.addEventListener('theme:loading:start', function () {
    if (!pending) return;
    var button = pending.button;
    pending = null;
    clearTimeout(quietTimer);
    clearTimeout(resetTimer);
    if (active && active !== button) resetButton(active);
    active = button;
    button.classList.add('gj-atc');
    stateEl(button).textContent = 'Adding';
    button.classList.remove('is-added');
    // Restart the hairline from zero
    void button.offsetWidth;
    button.classList.add('is-adding');
  });

  document.addEventListener('theme:loading:end', function () {
    // Give the loading bar time to finish hiding itself before it is allowed back
    setTimeout(function () { root.classList.remove('gj-quiet-load'); }, 600);
  });

  document.addEventListener('variant:add', function () {
    var button = active;
    if (!button) return;
    var el = stateEl(button);
    el.innerHTML = TICK + '<span>Added to your bag</span>';
    // Swap in one step so the hairline runs on from where it is to full width
    button.classList.add('is-added');
    button.classList.remove('is-adding');
    clearTimeout(resetTimer);
    resetTimer = setTimeout(function () { resetButton(button); }, 2200);
  });

  document.addEventListener('cart:error', function () {
    clearTimeout(resetTimer);
    resetButton(active);
    root.classList.remove('gj-quiet-load');
  });

  /* ---------------------------------------------------------------------------
     3. Bag drawer
     --------------------------------------------------------------------------- */
  var fromAdd = false;     // next drawer open was triggered by an add
  var oldFill = null;      // delivery bar width before the add, e.g. "42%"

  document.addEventListener('variant:add', function () {
    fromAdd = true;
    var fill = document.querySelector('cart-drawer [data-fsb-fill]');
    oldFill = fill ? (fill.style.width || '0%') : null;
  }, true);

  function animateFill(drawer, delay) {
    var fill = drawer.querySelector('[data-fsb-fill]');
    if (!fill || oldFill === null) return;
    var target = fill.style.width;
    var from = oldFill;
    oldFill = null;
    if (!target || target === from) return;
    fill.classList.remove('gj-fill-animate');
    fill.style.width = from;
    void fill.offsetWidth;
    setTimeout(function () {
      fill.classList.add('gj-fill-animate');
      fill.style.width = target;
    }, delay);
  }

  function staggerContents(drawer, startDelay) {
    var targets = [];
    var bar = drawer.querySelector('.free-shipping-bar, .fsb');
    if (bar) targets.push(bar);
    drawer.querySelectorAll('.cart-drawer__items > *').forEach(function (el) { targets.push(el); });
    var recs = drawer.querySelector('.gj-cart-recs');
    if (recs) targets.push(recs);

    targets.forEach(function (el, i) {
      el.classList.remove('is-in');
      el.setAttribute('data-gj-enter', '');
      setTimeout(function () { el.classList.add('is-in'); }, startDelay + 90 + Math.min(i, 6) * 80);
    });
    // Clean up so later re-renders are not left hidden
    setTimeout(function () {
      targets.forEach(function (el) { el.removeAttribute('data-gj-enter'); el.classList.remove('is-in'); });
    }, startDelay + 1600);

    if (bar) animateFill(drawer, startDelay + 260);
  }

  function controlsFrom(animations) {
    return {
      finished: Promise.all(animations.map(function (a) { return a.finished.catch(function () {}); })),
      finish: function () { animations.forEach(function (a) { try { a.finish(); } catch (e) {} }); }
    };
  }

  function patchDrawer() {
    var CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype.__gjMotion) return;
    var proto = CartDrawer.prototype;
    var originalEnter = proto.createEnterAnimationControls;
    var originalLeave = proto.createLeaveAnimationControls;
    proto.__gjMotion = true;

    proto.createEnterAnimationControls = function () {
      var overlay = this.getShadowPartByName && this.getShadowPartByName('overlay');
      var content = this.getShadowPartByName && this.getShadowPartByName('content');
      if (reduce.matches || !overlay || !content || !content.animate) {
        fromAdd = false;
        return originalEnter.call(this);
      }
      // Drop the held end state of the last close before opening again
      [overlay, content].forEach(function (el) { el.getAnimations().forEach(function (an) { an.cancel(); }); });
      if (getComputedStyle(content).position === 'static') content.style.position = 'relative';
      var fromRight = (this.getAttribute('open-from') || 'right') === 'right';
      content.style.marginInlineStart = fromRight ? 'auto' : 0;
      // After an add, wait a beat so the tick on the button is seen first
      var delay = fromAdd ? 420 : 0;
      fromAdd = false;
      var offscreen = 'translateX(calc(var(--transform-logical-flip, 1) * ' + (fromRight ? '100%' : '-100%') + '))';
      var a = overlay.animate([{ opacity: 0 }, { opacity: 1 }],
        { duration: 520, delay: delay, easing: EASE_IN_OUT, fill: 'backwards' });
      var b = content.animate([{ transform: offscreen }, { transform: 'translateX(0)' }],
        { duration: 620, delay: delay, easing: EASE_OUT, fill: 'backwards' });
      staggerContents(this, delay + 160);
      return controlsFrom([a, b]);
    };

    proto.createLeaveAnimationControls = function () {
      var overlay = this.getShadowPartByName && this.getShadowPartByName('overlay');
      var content = this.getShadowPartByName && this.getShadowPartByName('content');
      if (reduce.matches || !overlay || !content || !content.animate) return originalLeave.call(this);
      var fromRight = (this.getAttribute('open-from') || 'right') === 'right';
      var offscreen = 'translateX(calc(var(--transform-logical-flip, 1) * ' + (fromRight ? '100%' : '-100%') + '))';
      var a = overlay.animate([{ opacity: 1 }, { opacity: 0 }],
        { duration: 380, easing: EASE_IN_OUT, fill: 'forwards' });
      var b = content.animate([{ transform: 'translateX(0)' }, { transform: offscreen }],
        { duration: 400, easing: 'cubic-bezier(.55,0,.75,.2)', fill: 'forwards' });
      return controlsFrom([a, b]);
    };
  }

  if (customElements.get('cart-drawer')) patchDrawer();
  else customElements.whenDefined('cart-drawer').then(patchDrawer);

  /* ---------------------------------------------------------------------------
     4. Bag dot pulse
     --------------------------------------------------------------------------- */
  document.addEventListener('cart:change', function (event) {
    if (reduce.matches || !event.detail || event.detail.baseEvent !== 'variant:add') return;
    setTimeout(function () {
      document.querySelectorAll('.header__cart-dot').forEach(function (dot) {
        dot.classList.remove('gj-pulse');
        void dot.offsetWidth;
        dot.classList.add('gj-pulse');
        dot.addEventListener('animationend', function () { dot.classList.remove('gj-pulse'); }, { once: true });
      });
    }, 150);
  });

  /* ---------------------------------------------------------------------------
     5. Images fade up from a sand ground as they load
     --------------------------------------------------------------------------- */
  var IMG_SCOPE = '.product-card__image--primary, .product-gallery__media img, main .shopify-section img[loading="lazy"]';
  var IMG_SKIP = '.product-card__image--secondary, image-parallax img, .slideshow img, .marquee img, [class*="logo"] img';

  function settle(img) {
    img.classList.add('is-loaded');
    setTimeout(function () { img.classList.remove('gj-img-fade', 'is-loaded'); }, 1500);
  }

  var pageSettled = false;
  window.addEventListener('load', function () { setTimeout(function () { pageSettled = true; }, 0); });

  function prepareImages(scope) {
    if (reduce.matches) return;
    scope.querySelectorAll(IMG_SCOPE).forEach(function (img) {
      if (img.dataset.gjImg || img.matches(IMG_SKIP)) return;
      img.dataset.gjImg = '1';
      if (img.complete && img.naturalWidth) return;   // already there: never hide it
      // On screen when the page opens: show it the moment it arrives (no fade), so the first view and the
      // main image are never held back. Only photos further down fade in as they load.
      var box = img.getBoundingClientRect();
      if (!pageSettled && box.top < window.innerHeight && box.bottom > 0 && box.width > 0) return;
      img.classList.add('gj-img-fade');
      var done = function () { requestAnimationFrame(function () { settle(img); }); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  /* ---------------------------------------------------------------------------
     6. Scroll reveals: headings, then product cards in a soft stagger.
     Cards in a horizontal carousel reveal together when the carousel arrives
     (cards off to the side would otherwise never be "seen").
     --------------------------------------------------------------------------- */
  var REVEAL = 'main .shopify-section:not(:first-child):not(.shopify-section--main-product) :is(h2, .h1, .h2):not(product-card *, .slideshow *), ' +
               'main .shopify-section:not(.shopify-section--main-product) product-card';
  var groups = new Map();   // observed target -> elements to reveal
  var revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(onReveal, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function scrollerOf(el) {
    var node = el.parentElement;
    while (node && !node.classList.contains('shopify-section')) {
      var ox = getComputedStyle(node).overflowX;
      if (ox === 'auto' || ox === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  }

  function prepareReveals(scope) {
    if (reduce.matches || !revealObserver) return;
    var fold = window.innerHeight;
    scope.querySelectorAll(REVEAL).forEach(function (el) {
      if (el.dataset.gjReveal) return;
      el.dataset.gjReveal = '1';
      if (el.getBoundingClientRect().top < fold) return;   // on screen at load: leave it be
      var target = (el.tagName === 'PRODUCT-CARD' && scrollerOf(el)) || el;
      el.classList.add('gj-reveal');
      if (!groups.has(target)) { groups.set(target, []); revealObserver.observe(target); }
      groups.get(target).push(el);
    });
  }

  function onReveal(entries) {
    var batch = [];
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      revealObserver.unobserve(entry.target);
      batch = batch.concat(groups.get(entry.target) || []);
      groups.delete(entry.target);
    });
    batch.sort(function (a, b) { return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1; });
    batch.forEach(function (el, i) {
      el.style.setProperty('--gj-reveal-delay', (Math.min(i, 7) * 0.07) + 's');
      el.classList.add('is-in');
      setTimeout(function () {
        el.classList.remove('gj-reveal', 'is-in');
        el.style.removeProperty('--gj-reveal-delay');
      }, 1700 + Math.min(i, 7) * 70);
    });
  }

  /* Jumping past content (anchor links, restored scroll) never "intersects":
     reveal anything that is now above the bottom of the screen */
  var catchUpTick = false;
  window.addEventListener('scroll', function () {
    if (catchUpTick || !groups.size) return;
    catchUpTick = true;
    requestAnimationFrame(function () {
      catchUpTick = false;
      var passed = [];
      groups.forEach(function (els, target) {
        if (target.getBoundingClientRect().top < window.innerHeight) passed.push({ target: target, isIntersecting: true });
      });
      if (passed.length) onReveal(passed);
    });
  }, { passive: true });

  /* ---------------------------------------------------------------------------
     8. Product cards on touch screens: swipe through up to 4 photos.
     Switched on/off in theme settings (gj_card_swipe_mobile), which decides whether
     the <template> of extra photos is output at all. Grid cards only: inside a
     sideways carousel a second swipe direction would fight the carousel.
     --------------------------------------------------------------------------- */
  var touchCards = window.matchMedia('(hover: none) and (max-width: 999px)');

  function prepareCardSwipes(scope) {
    if (!touchCards.matches) return;
    scope.querySelectorAll('template[data-gj-card-swipe]').forEach(function (tpl) {
      var media = tpl.closest('.product-card__media');
      if (!media || media.dataset.gjSwipe) return;
      media.dataset.gjSwipe = '1';
      if (scrollerOf(media)) return;
      var primary = media.querySelector('.product-card__image--primary');
      if (!primary) return;

      // The strip and the main photo's slide are written by the card snippet; older markup gets them built here
      var track = media.querySelector('[data-gj-swipe-track]');
      var prebuilt = !!track;
      if (!track) {
        track = document.createElement('div');
        track.className = 'gj-card-swipe';
        primary.parentNode.insertBefore(track, primary);
      }
      track.classList.add('is-live');
      // Extra photos are parked (no src) until the card is first touched
      var images = [primary].concat(Array.prototype.slice.call(tpl.content.querySelectorAll('img')).map(function (img) {
        var copy = img.cloneNode(true);
        copy.dataset.gjSrcset = copy.getAttribute('srcset') || '';
        copy.dataset.gjSrc = copy.getAttribute('src') || '';
        copy.removeAttribute('srcset');
        copy.removeAttribute('src');
        return copy;
      }));
      images.forEach(function (img, n) {
        if (n === 0 && prebuilt) return;   // already in its slide: never move it
        var slide = document.createElement('div');
        slide.className = 'gj-card-swipe__slide';
        slide.appendChild(img);
        track.appendChild(slide);
      });

      var dashes = document.createElement('div');
      dashes.className = 'gj-card-swipe__dashes';
      dashes.setAttribute('aria-hidden', 'true');
      dashes.innerHTML = images.map(function (_, i) { return '<span' + (i === 0 ? ' class="is-active"' : '') + '></span>'; }).join('');
      media.appendChild(dashes);
      media.classList.add('has-gj-swipe');

      var woken = false;
      function wake() {
        if (woken) return;
        woken = true;
        images.slice(1).forEach(function (img) {
          if (!reduce.matches) img.classList.add('gj-img-fade');
          img.addEventListener('load', function () { requestAnimationFrame(function () { settle(img); }); }, { once: true });
          img.dataset.gjImg = '1';
          if (img.dataset.gjSrcset) img.setAttribute('srcset', img.dataset.gjSrcset);
          img.setAttribute('src', img.dataset.gjSrc);
        });
      }
      track.addEventListener('touchstart', wake, { passive: true });
      track.addEventListener('pointerdown', wake, { passive: true });

      var current = 0, tick = false;
      track.addEventListener('scroll', function () {
        wake();
        if (tick) return;
        tick = true;
        requestAnimationFrame(function () {
          tick = false;
          var index = Math.round(track.scrollLeft / (track.clientWidth || 1));
          if (index === current) return;
          current = index;
          Array.prototype.forEach.call(dashes.children, function (dash, i) { dash.classList.toggle('is-active', i === index); });
        });
      }, { passive: true });
    });
  }

  /* New content (filters, recommendations, section reloads) gets the same treatment */
  var pendingScan = false;
  function scan() {
    pendingScan = false;
    prepareCardSwipes(document);
    prepareImages(document);
    prepareReveals(document);
  }

  function queueScan() {
    if (pendingScan) return;
    pendingScan = true;
    requestAnimationFrame(scan);
  }

  scan();
  var main = document.querySelector('main') || document.body;
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].addedNodes.length) { queueScan(); return; }
    }
  }).observe(main, { childList: true, subtree: true });
  document.addEventListener('cart:change', function () { setTimeout(function () { prepareImages(document); }, 0); });

  /* ---------------------------------------------------------------------------
     7. Product page: photos and price ease in when the variant changes
     --------------------------------------------------------------------------- */
  document.addEventListener('variant:change', function (event) {
    if (reduce.matches) return;
    var detail = event.detail || {};
    var now = detail.variant && detail.variant.featured_media;
    var before = detail.previousVariant && detail.previousVariant.featured_media;
    if (!now || !before || now.id === before.id) return;
    var form = document.getElementById(detail.formId);
    var section = form ? form.closest('.shopify-section') : null;
    var carousel = section && section.querySelector('.product-gallery__carousel');
    if (carousel && carousel.animate) {
      carousel.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 650, easing: EASE_OUT });
    }
  });

  var priceObserver = new MutationObserver(function (records) {
    if (reduce.matches) return;
    var seen = new Set();
    records.forEach(function (r) {
      var el = (r.target.nodeType === 1 ? r.target : r.target.parentElement);
      el = el && el.closest('.price-list');
      if (!el) {
        // The whole price block was swapped in by a re-render
        for (var i = 0; i < r.addedNodes.length && !el; i++) {
          var n = r.addedNodes[i];
          if (n.nodeType === 1) el = n.matches('.price-list') ? n : n.querySelector('.product-info .price-list');
        }
      }
      if (el && !seen.has(el) && el.animate) {
        seen.add(el);
        el.animate([{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 380, easing: EASE_OUT });
      }
    });
  });
  document.querySelectorAll('.shopify-section--main-product product-rerender').forEach(function (el) {
    priceObserver.observe(el, { childList: true, characterData: true, subtree: true });
  });

  /* ---------------------------------------------------------------------------
     9. Footer (sections/gj-footer.liquid): the name fills the width exactly and
     rises in when reached; London time ticks over.
     --------------------------------------------------------------------------- */
  function fitWordmarks() {
    document.querySelectorAll('[data-gj-wordmark]').forEach(function (word) {
      var box = word.parentElement;
      var available = box.clientWidth;
      if (!available) return;
      word.style.fontSize = '100px';
      var width = word.getBoundingClientRect().width;
      if (width) word.style.fontSize = (100 * available / width).toFixed(2) + 'px';
    });
  }

  fitWordmarks();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitWordmarks);
  var fitTimer;
  window.addEventListener('resize', function () { clearTimeout(fitTimer); fitTimer = setTimeout(fitWordmarks, 120); });

  if (!reduce.matches && 'IntersectionObserver' in window) {
    document.querySelectorAll('.gj-footer__word').forEach(function (box) {
      if (box.getBoundingClientRect().top < window.innerHeight) return;   // already on screen
      box.classList.add('is-waiting');
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        box.classList.add('is-in');
        box.classList.remove('is-waiting');
      }, { threshold: 0.2 });
      io.observe(box);
    });
  }

  function tickClocks() {
    document.querySelectorAll('[data-gj-clock]').forEach(function (el) {
      try {
        el.textContent = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: el.dataset.tz || 'Europe/London' }).format(new Date());
      } catch (e) {}
    });
  }
  if (document.querySelector('[data-gj-clock]')) { tickClocks(); setInterval(tickClocks, 20000); }

  /* ---------------------------------------------------------------------------
     10. Category index (sections/gj-home-category-index.liquid).
     Desktop: resting on a name (short hover intent, so sweeping the list doesn't flicker)
     dissolves its photo in over the previous one. Phones show a plain contents list (CSS only).
     --------------------------------------------------------------------------- */
  document.querySelectorAll('[data-gj-catidx]').forEach(function (idx) {
    var items = Array.prototype.slice.call(idx.querySelectorAll('.gj-catidx__item'));
    var imgs = Array.prototype.slice.call(idx.querySelectorAll('.gj-catidx__img'));
    var current = -1;

    function imgFor(i) {
      for (var n = 0; n < imgs.length; n++) if (Number(imgs[n].dataset.index) === i) return imgs[n];
      return null;
    }

    function show(i, instant) {
      if (i === current) return;
      var prev = imgFor(current);
      var next = imgFor(i);
      current = i;
      items.forEach(function (el, n) { el.classList.toggle('is-on', n === i); });
      if (next && next.loading === 'lazy') next.loading = 'eager';
      imgs.forEach(function (el) { if (el !== prev && el !== next) el.classList.remove('is-on', 'is-prev', 'is-entering'); });
      if (prev && prev !== next) { prev.classList.remove('is-on', 'is-entering'); prev.classList.add('is-prev'); }
      if (!next) return;
      next.classList.remove('is-prev');
      if (instant || reduce.matches) { next.classList.add('is-on'); return; }
      next.classList.add('is-on', 'is-entering');
      void next.offsetWidth;
      requestAnimationFrame(function () { next.classList.remove('is-entering'); });
    }

    show(0, true);
    idx.classList.add('is-ready');

    var intent;
    items.forEach(function (item, i) {
      item.addEventListener('mouseenter', function () {
        clearTimeout(intent);
        intent = setTimeout(function () { show(i); }, 110);
      });
      item.addEventListener('mouseleave', function () { clearTimeout(intent); });
      item.addEventListener('focus', function () { show(i); });
    });

    // Warm up the other photos once the section is close, so a change never shows an empty frame
    if ('IntersectionObserver' in window) {
      var warm = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        warm.disconnect();
        imgs.forEach(function (img) { if (img.loading === 'lazy') img.loading = 'eager'; });
      }, { rootMargin: '800px 0px' });
      warm.observe(idx);
    }

  });

  /* ---------------------------------------------------------------------------
     11. One calm timing for every panel the theme opens (it defaults to 0.15–0.3s):
     drawers (filters, mobile menu, any x-drawer), the search panel, desktop dropdown
     and mega menus, and the mobile menu's links. The bag drawer keeps its own
     sequence (section 3). Switched off for "reduce motion".
     --------------------------------------------------------------------------- */
  function run(el, frames, opts) {
    return el && el.animate ? el.animate(frames, opts) : null;
  }

  function controlsOf(list) {
    list = list.filter(Boolean);
    return {
      finished: Promise.all(list.map(function (a) { return a.finished.catch(function () {}); })).then(function () { return true; }),
      finish: function () { list.forEach(function (a) { try { a.finish(); } catch (e) {} }); }
    };
  }

  /* The theme's own animations leave an inline transform on the panel, which keeps it painted above
     the (fixed) blurred backdrop. Ours finish clean, so give a static panel its own layer instead. */
  function keepAbove(el) {
    if (el && getComputedStyle(el).position === 'static') el.style.position = 'relative';
  }

  function cancelHeld(els) {
    els.forEach(function (el) { if (el && el.getAnimations) el.getAnimations().forEach(function (a) { a.cancel(); }); });
  }

  function stagger(nodes, frames, base, step, duration) {
    return Array.prototype.map.call(nodes, function (el, i) {
      return run(el, frames, { duration: duration, delay: base + Math.min(i, 8) * step, easing: EASE_OUT, fill: 'backwards' });
    });
  }

  function patchOverlays() {
    if (reduce.matches) return;

    /* Drawers: same glide as the bag, without the bag's content sequence */
    var CartDrawer = customElements.get('cart-drawer');
    var drawerProto = CartDrawer && Object.getPrototypeOf(CartDrawer.prototype);
    if (drawerProto && !drawerProto.__gjMotion && Object.prototype.hasOwnProperty.call(drawerProto, 'createEnterAnimationControls')) {
      drawerProto.__gjMotion = true;
      drawerProto.createEnterAnimationControls = function () {
        var overlay = this.getShadowPartByName('overlay');
        var content = this.getShadowPartByName('content');
        cancelHeld([overlay, content]);
        keepAbove(content);
        var fromRight = (this.getAttribute('open-from') || 'right') === 'right';
        if (content) content.style.marginInlineStart = fromRight ? 'auto' : 0;
        var off = 'translateX(calc(var(--transform-logical-flip, 1) * ' + (fromRight ? '100%' : '-100%') + '))';
        return controlsOf([
          run(overlay, [{ opacity: 0 }, { opacity: 1 }], { duration: 480, easing: EASE_IN_OUT, fill: 'backwards' }),
          run(content, [{ transform: off }, { transform: 'translateX(0)' }], { duration: 600, easing: EASE_OUT, fill: 'backwards' })
        ]);
      };
      drawerProto.createLeaveAnimationControls = function () {
        var overlay = this.getShadowPartByName('overlay');
        var content = this.getShadowPartByName('content');
        var fromRight = (this.getAttribute('open-from') || 'right') === 'right';
        var off = 'translateX(calc(var(--transform-logical-flip, 1) * ' + (fromRight ? '100%' : '-100%') + '))';
        return controlsOf([
          run(overlay, [{ opacity: 1 }, { opacity: 0 }], { duration: 380, easing: EASE_IN_OUT, fill: 'forwards' }),
          run(content, [{ transform: 'translateX(0)' }, { transform: off }], { duration: 400, easing: 'cubic-bezier(.55,0,.75,.2)', fill: 'forwards' })
        ]);
      };
    }

    /* Mobile menu: links settle in one after another, unhurried */
    var Sidebar = customElements.get('header-sidebar');
    if (Sidebar && !Sidebar.prototype.__gjMotion) {
      Sidebar.prototype.__gjMotion = true;
      Sidebar.prototype.revealItems = function (withDelay) {
        var panel = this.querySelector('.header-sidebar__main-panel');
        var wait = withDelay ? 450 : 0;
        if (panel) {
          cancelHeld([panel]);
          setTimeout(function () { panel.style.opacity = '1'; panel.style.transform = 'none'; }, wait);
        }
        var items = this.querySelectorAll('.header-sidebar__main-panel .header-sidebar__linklist li');
        var footer = this.querySelector('.header-sidebar__footer');
        return controlsOf(
          stagger(items, [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], wait + 60, 55, 560)
            .concat([run(footer, [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 560, delay: wait + 60 + Math.min(items.length, 8) * 55, easing: EASE_OUT, fill: 'backwards' })])
        );
      };
    }

    /* Search panel: drops in softly from under the header */
    var Search = customElements.get('header-search');
    if (Search && !Search.prototype.__gjMotion && Object.prototype.hasOwnProperty.call(Search.prototype, 'createEnterAnimationControls')) {
      Search.prototype.__gjMotion = true;
      Search.prototype.createEnterAnimationControls = function () {
        var overlay = this.getShadowPartByName('overlay');
        var content = this.getShadowPartByName('content');
        cancelHeld([overlay, content]);
        keepAbove(content);
        return controlsOf([
          run(overlay, [{ opacity: 0 }, { opacity: 1 }], { duration: 450, easing: EASE_IN_OUT, fill: 'backwards' }),
          run(content, [{ opacity: 0, transform: 'translateY(-14px)' }, { opacity: 1, transform: 'none' }], { duration: 520, easing: EASE_OUT, fill: 'backwards' })
        ]);
      };
      Search.prototype.createLeaveAnimationControls = function () {
        var overlay = this.getShadowPartByName('overlay');
        var content = this.getShadowPartByName('content');
        return controlsOf([
          run(overlay, [{ opacity: 1 }, { opacity: 0 }], { duration: 320, easing: EASE_IN_OUT, fill: 'forwards' }),
          run(content, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-10px)' }], { duration: 320, easing: EASE_IN_OUT, fill: 'forwards' })
        ]);
      };
    }

    /* Desktop dropdown and mega menus: a soft fade, then the links settle in */
    [['dropdown-menu-disclosure', ':scope > li'], ['mega-menu-disclosure', '.mega-menu__linklist > li']].forEach(function (pair) {
      var Menu = customElements.get(pair[0]);
      if (!Menu || Menu.prototype.__gjMotion) return;
      Menu.prototype.__gjMotion = true;
      Menu.prototype.createShowAnimationControls = function () {
        var content = this.contentElement;
        if (!content) return controlsOf([]);
        cancelHeld([content]);
        var list = [run(content, [{ opacity: 0 }, { opacity: 1 }], { duration: 380, easing: EASE_OUT, fill: 'backwards' })];
        list = list.concat(stagger(content.querySelectorAll(pair[1]), [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], 60, 45, 520));
        var promo = content.querySelector('.mega-menu__promo');
        if (promo) list.push(run(promo, [{ opacity: 0 }, { opacity: 1 }], { duration: 700, delay: 160, easing: EASE_OUT, fill: 'backwards' }));
        return controlsOf(list);
      };
      Menu.prototype.createHideAnimationControls = function () {
        var content = this.contentElement;
        if (!content) return controlsOf([]);
        // held at 0 until the theme closes the menu; the next open clears it
        return controlsOf([run(content, [{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: EASE_IN_OUT, fill: 'forwards' })]);
      };
    });
  }

  ['cart-drawer', 'header-sidebar', 'header-search', 'dropdown-menu-disclosure', 'mega-menu-disclosure'].forEach(function (name) {
    if (customElements.get(name)) patchOverlays();
    else customElements.whenDefined(name).then(patchOverlays);
  });

  /* ---------------------------------------------------------------------------
     12. Guard for a theme bug: a carousel with no slides yet (e.g. Recently viewed before it fills) throws on
     window resize when it asks its first slide for its alignment. Fall back to 'start' when there is none.
     --------------------------------------------------------------------------- */
  function guardCarousel() {
    var Carousel = customElements.get('scroll-carousel');
    if (!Carousel || Carousel.prototype.__gjGuard) return;
    var desc = Object.getOwnPropertyDescriptor(Carousel.prototype, 'cellAlign');
    if (!desc || !desc.get) return;
    Carousel.prototype.__gjGuard = true;
    Object.defineProperty(Carousel.prototype, 'cellAlign', {
      configurable: true,
      get: function () {
        var cells = this.cells;
        if (!cells || !cells[0]) return 'start';
        return desc.get.call(this);
      }
    });
  }
  if (customElements.get('scroll-carousel')) guardCarousel();
  else customElements.whenDefined('scroll-carousel').then(guardCarousel);
})();
